/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* eslint-disable react/no-unused-state */
import React from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import { FormattedMessage } from 'react-intl';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/esm/Clear';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import JSG from '@cedalo/jsg-ui';

import ColorComponent from '../ColorComponent';
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import DialogButton from '../../utils/DialogButton';

let uniqueId = 0;

export default class ValueRangesDialog extends React.Component {
	static propTypes = {
		open: PropTypes.bool.isRequired,
		stateHandler: PropTypes.func.isRequired,
		ranges: PropTypes.array.isRequired,
		sheetView: PropTypes.object.isRequired,
		chart: PropTypes.object
	};

	static defaultProps = {
		chart: undefined
	};

	constructor(props) {
		super(props);

		this.state = {
			open: props.open,
			ranges: this.copyRanges(props.ranges),
			showColor: false,
			anchorEl: undefined,
			error: '',
			errorId: ''
		};

		this.handleClose = this.handleClose.bind(this);
	}

	// eslint-disable-next-line camelcase
	UNSAFE_componentWillReceiveProps(nextProps) {
		// You don't have to do this check first, but it can help prevent an unneeded render
		if (nextProps.open === true && this.props.open === false) {
			this.setState({ ranges: this.copyRanges(nextProps.ranges) });
		}

		this.setState({ open: nextProps.open });
	}

	copyRanges(ranges) {
		const copyRanges = [];
		uniqueId = 0;
		ranges.forEach((range) => {
			const copyRange = {};
			copyRange.id = uniqueId;
			copyRange.formula = range.formula.copy();
			copyRange.formula.evaluate(this.props.sheetView.getItem());
			// term is set to undefined ?!
			range.formula.evaluate(this.props.sheetView.getItem());
			copyRange.format = range.format.copy();
			copyRanges.push(copyRange);
			uniqueId += 1;
		});

		return copyRanges;
	}

	handleDelete = (row) => () => {
		JSG.Arrays.remove(this.state.ranges, row);
		this.setState({ranges: this.state.ranges});
	};

	getError(error) {
		switch (error) {
		case 'number':
			return (
				<FormattedMessage
					id="DialogValueRanges.Numeric"
					defaultMessage="Only Number allowed!"
				/>
			)
		case 'formula':
			return (
				<FormattedMessage
					id="DialogValueRanges.Formula"
					defaultMessage="Invalid Formula!"
				/>
			)
		default:
			break;
		}
		return null;
	}

	replaceTerm(event, expression, index, errorId, number) {
		const item = this.props.sheetView.getItem();
		const formula = event.target.value;
		let newExpression;

		try {
			newExpression = item.textToExpression(formula);
		} catch (e) {
			this.setState({
				error: this.getError('formula'),
				errorId
			});
			return;
		}

		if (number && !newExpression.expression.hasFormula() && !(newExpression.expression instanceof JSG.NumberExpression)) {
			this.setState({
				error: this.getError('number'),
				errorId
			});
			return;
		}

		const term = expression.getTerm();

		if (term && newExpression) {
			for (let i = term.params.length; i < index; i += 1) {
				term.params[i] = new JSG.NullTerm();
			}

			term.params[index] = newExpression.term;
			expression.correctFormula(item.model, true);
		}

		this.setState({error: '', errorId: ''});
	}

	handleBlur = (event, row, index, errorId, number) => {
		this.replaceTerm(event, row.formula, index, errorId, number);
	};

	getParamText(term, index) {
		if (term && term.params && term.params.length > index) {
			return term.params[index].toString({ item: this.props.sheetView.getItem(), useName: true });
		}

		return undefined;
	}

	getFormulaParameter(expr, index) {
		const term = expr.getTerm();
		if (term && term.params && term.params.length > index) {
			const param = term.params[index];
			if (param.isStatic) {
				let str = param.toLocaleString(JSG.getParserLocaleSettings());
				str = str.replace(/^"(.+(?="$))"$/, '$1');
				return str;
			} else {
				const item = this.props.sheetView.getItem();
				return `=${param.toString({item, useName: true})}`;
			}
		}
		return '';
	}


	handleAdd = () => {
		const formula = new JSG.Expression(0, `VALUERANGE("Label",0,0,0)`);
		formula.evaluate(this.props.sheetView.getItem());

		this.state.ranges.push({
			formula,
			format: new JSG.ChartFormat(undefined, undefined, undefined, undefined, '#FF0000'),
			id: uniqueId,
		});

		uniqueId += 1;
		this.setState({ranges: this.state.ranges});
	};

	onColor = (color, row) => {
		// } else if (color.hex.toUpperCase() === '#FFFFFE') {

		row.format.fillColor = JSG.GraphUtils.colorFromRGB(color.r, color.g, color.b);
		row.format.transparency = color.a * 100;

		this.setState({
			showColor: false
		});
	};

	handleCancel() {
		this.props.stateHandler(false);
	}

	handleClose() {
		this.props.stateHandler(false, this.state.ranges);
	}

	render() {
		return (
            <Dialog open={this.state.open} onClose={() => this.handleCancel()} maxWidth={false}>
				<DialogTitle>
					<FormattedMessage id="DialogValueRanges.Title" defaultMessage="Edit Value Ranges" />
				</DialogTitle>
				<DialogContent
					style={{
						height: '480px',
						width: '815px',
					}}
				>
					<div
						style={{
							overflowY: 'auto',
							border: '1px solid lightgrey',
							height: '390px',
							marginTop: '23px',
						}}
					>
					<Table>
						<TableHead>
							<TableRow
								style={{
									height: '35px',
								}}
							>
								<TableCell
									key="h1"
									style={{
										padding: '4px 10px 4px 10px',
									}}
								>
									<FormattedMessage id="DialogValueRanges.Label" defaultMessage="Label" />
								</TableCell>
								{this.props.chart && this.props.chart.type === 'waterfall' ? null : [
									<TableCell
										key="h2"
										style={{
											padding: '4px 10px 4px 10px',
										}}
									>
										<FormattedMessage id="DialogValueRanges.From" defaultMessage="From"/>
									</TableCell>,
									<TableCell
										key="h3"
										style={{
											padding: '4px 10px 4px 10px',
										}}
									>
										<FormattedMessage id="DialogValueRanges.To" defaultMessage="To" />
									</TableCell>
								]}
								{this.props.chart ? null :
								<TableCell
									key="h4"
									style={{
										padding: '4px 10px 4px 10px',
									}}
								>
									<FormattedMessage id="DialogValueRanges.Width" defaultMessage="Width" />
								</TableCell>}
								<TableCell
									key="h5"
									style={{
										padding: '4px 10px 4px 10px',
									}}
								>
									<FormattedMessage id="DialogValueRanges.Color" defaultMessage="Color" />
								</TableCell>
								<TableCell
									key="h6"
									style={{
										width: '30px',
									}}
								/>
							</TableRow>
						</TableHead>
						<TableBody>
							{this.state.ranges && this.state.ranges.map((range) =>
								(
									<TableRow key={range.id}>
										<TableCell
											key={1}
											style={{
												padding: '4px 10px 4px 10px',
											}}
										>
											<TextField
												variant="standard"
												fullWidth
												margin="dense"
												error={this.state.errorId === `${range.id}&label` && !!this.state.error}
												helperText={this.state.errorId === `${range.id}&label` ? this.state.error : ''}
												defaultValue={this.getFormulaParameter(range.formula, 0)}
												id={`${range.id}&label`}
												onBlur={(ev) => this.handleBlur(ev, range, 0, `${range.id}&label`, false)}

											/>
										</TableCell>
										{this.props.chart && this.props.chart.type === 'waterfall' ? null : [
											<TableCell
												key={3}
												style={{
													padding: '4px 10px 4px 10px',
												}}
											>
												<TextField
													variant="standard"
													fullWidth
													margin="dense"
													error={this.state.errorId === `${range.id}&from` && !!this.state.error}
													helperText={this.state.errorId === `${range.id}&from` ? this.state.error : ''}
													defaultValue={this.getFormulaParameter(range.formula, 1)}
													id={`${range.id}&from`}
													onBlur={(ev) => this.handleBlur(ev, range, 1, `${range.id}&from`, true)}

												/>
											</TableCell>,
											<TableCell
												key={5}
												style={{
													padding: '4px 10px 4px 10px',
												}}
											>
												<TextField
													variant="standard"
													fullWidth
													margin="dense"
													id={`${range.id}&to`}
													error={this.state.errorId === `${range.id}&to` && !!this.state.error}
													helperText={this.state.errorId === `${range.id}&to` ? this.state.error : ''}
													defaultValue={this.getFormulaParameter(range.formula, 2)}
													onBlur={(ev) => this.handleBlur(ev, range, 2, `${range.id}&to`, true)}
													onKeyPress={(ev) => {
														if (ev.key === 'Enter') {
															this.handleBlur(ev, range, 'to');
														}
													}}
												/>
											</TableCell>
										]}
										{this.props.chart ? null :
										<TableCell
											key={4}
											style={{
												padding: '4px 10px 4px 10px',
											}}
										>
											<TextField
												variant="standard"
												fullWidth
												margin="dense"
												error={this.state.errorId === `${range.id}&width` && !!this.state.error}
												helperText={this.state.errorId === `${range.id}&width` ? this.state.error : ''}
												defaultValue={this.getFormulaParameter(range.formula, 3)}
												id={`${range.id}&width`}
												onBlur={(ev) => this.handleBlur(ev, range, 3, `${range.id}&width`, true)}

											/>
										</TableCell>}
										<TableCell
											key={2}
											style={{
												padding: '4px 10px 4px 10px',
											}}
										>
											<FormControl size="small" variant="outlined" margin="normal" style={{width: '120px' }}>
												<ColorComponent
													label={
														<FormattedMessage
															id="DialogValueRanges.Color"
															defaultMessage="Color"
														/>
													}
													disableAlpha={false}
													color={range.format.fillColorRGBA}
													onChange={(color) => this.onColor(color, range)}
												/>
											</FormControl>
										</TableCell>
										<TableCell
											key={6}
											style={{
												width: '30px',
											}}
										>
											<IconButton id={range.id} onClick={this.handleDelete(range)} size="large">
												<DeleteIcon
													style={{
														width: '20px',
														height: '20px',
													}}
												/>
											</IconButton>
										</TableCell>
									</TableRow>
								),
							)}
						</TableBody>
					</Table>
					</div>
					<Typography style={{marginTop: '22px'}}>
						<FormattedMessage id="DialogValueRanges.Hint" defaultMessage="Formula" />
					</Typography>
				</DialogContent>
				<Divider />
				<DialogActions
					style={{
						justifyContent: 'space-between',
					}}
				>
					<DialogButton margin onClick={this.handleAdd}>
						<FormattedMessage id="DialogValueRanges.Add" defaultMessage="Add" />
					</DialogButton>
					<div>
						<DialogButton margin onClick={() => this.handleCancel()}>
							<FormattedMessage id="Dialog.Cancel" defaultMessage="Cancel" />
						</DialogButton>
						<DialogButton
							margin
							// disabled={this.state.closeDisabled || !globalTableData.reduce(verifyNames, true)}
							onClick={this.handleClose}
							disabled={this.state.error !== ''}
							defaultButton
						>
							<FormattedMessage id="DialogValueRanges.Save" defaultMessage="Save" />
						</DialogButton>
					</div>
				</DialogActions>
			</Dialog>
        );
	}
}
